import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { apiClient, Guest, RSVPData, RSVPResponse, handleApiError } from '../services/api';
import { useAuth } from './AuthContext';

interface RSVPContextType {
  // Guest state
  selectedGuest: Guest | null;
  loadingGuestSearch: boolean;
  guestSearchError: string | null;
  
  // RSVP state
  currentRSVP: RSVPResponse | null;
  loadingRSVP: boolean;
  submittingRSVP: boolean;
  rsvpError: string | null;
  
  // Actions
  searchGuest: (guestName: string) => Promise<void>;
  loadGuestById: (guestId: string) => Promise<void>;
  submitRSVP: (data: RSVPData) => Promise<boolean>;
  updateRSVP: (id: string, data: Partial<RSVPData>) => Promise<boolean>;
  clearSelection: () => void;
}

const RSVPContext = createContext<RSVPContextType | undefined>(undefined);

export const useRSVP = () => {
  const context = useContext(RSVPContext);
  if (context === undefined) {
    throw new Error('useRSVP must be used within an RSVPProvider');
  }
  return context;
};

interface RSVPProviderProps {
  children: ReactNode;
}

export const RSVPProvider: React.FC<RSVPProviderProps> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  
  // Guest state
  const [selectedGuest, setSelectedGuest] = useState<Guest | null>(null);
  const [loadingGuestSearch, setLoadingGuestSearch] = useState(false);
  const [guestSearchError, setGuestSearchError] = useState<string | null>(null);
  
  // RSVP state
  const [currentRSVP, setCurrentRSVP] = useState<RSVPResponse | null>(null);
  const [loadingRSVP, setLoadingRSVP] = useState(false);
  const [submittingRSVP, setSubmittingRSVP] = useState(false);
  const [rsvpError, setRsvpError] = useState<string | null>(null);

  const searchGuest = useCallback(async (guestName: string) => {
    if (!isAuthenticated) return;
    
    try {
      setLoadingGuestSearch(true);
      setGuestSearchError(null);
      const searchResponse = await apiClient.searchGuest(guestName);
      
      if (searchResponse.found && searchResponse.guest) {
        setSelectedGuest(searchResponse.guest);
        
        // Use RSVP data from the guest response if it exists
        setCurrentRSVP(searchResponse.guest.rsvp || null);
      } else {
        setGuestSearchError(searchResponse.message || 'Guest not found');
      }
    } catch (error) {
      setGuestSearchError(handleApiError(error));
    } finally {
      setLoadingGuestSearch(false);
    }
  }, [isAuthenticated]);

  const loadGuestById = useCallback(async (guestId: string) => {
    if (!isAuthenticated) return;
    
    try {
      setLoadingGuestSearch(true);
      setGuestSearchError(null);
      const response = await apiClient.getGuest(guestId);
      
      // Handle the GuestSearchResponse format
      if (response.found && response.guest) {
        setSelectedGuest(response.guest);
        
        // Use RSVP data from the guest response if it exists
        setCurrentRSVP(response.guest.rsvp || null);
      } else {
        setGuestSearchError(response.message || 'Guest not found');
      }
    } catch (error) {
      setGuestSearchError(handleApiError(error));
    } finally {
      setLoadingGuestSearch(false);
    }
  }, [isAuthenticated]);

  const submitRSVP = async (data: RSVPData): Promise<boolean> => {
    try {
      setSubmittingRSVP(true);
      setRsvpError(null);
      
      const response = await apiClient.submitRSVP(data);
      setCurrentRSVP(response);
      
      // Update the guest's hasResponded status
      if (selectedGuest) {
        setSelectedGuest({ ...selectedGuest, hasResponded: true });
      }
      
      return true;
    } catch (error) {
      setRsvpError(handleApiError(error));
      return false;
    } finally {
      setSubmittingRSVP(false);
    }
  };

  const updateRSVP = async (id: string, data: Partial<RSVPData>): Promise<boolean> => {
    try {
      setSubmittingRSVP(true);
      setRsvpError(null);
      
      const response = await apiClient.updateRSVP(id, data);
      setCurrentRSVP(response);
      
      return true;
    } catch (error) {
      setRsvpError(handleApiError(error));
      return false;
    } finally {
      setSubmittingRSVP(false);
    }
  };

  const clearSelection = () => {
    setSelectedGuest(null);
    setCurrentRSVP(null);
    setGuestSearchError(null);
    setRsvpError(null);
  };

  // Load guests when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      // Initial load of guests is no longer needed, as guests are searched on demand
    } else {
      // Clear state when not authenticated
      setSelectedGuest(null);
      setCurrentRSVP(null);
      setGuestSearchError(null);
      setRsvpError(null);
    }
  }, [isAuthenticated]);

  const contextValue: RSVPContextType = {
    // Guest state
    selectedGuest,
    loadingGuestSearch,
    guestSearchError,
    
    // RSVP state
    currentRSVP,
    loadingRSVP,
    submittingRSVP,
    rsvpError,
    
    // Actions
    searchGuest,
    loadGuestById,
    submitRSVP,
    updateRSVP,
    clearSelection,
  };

  return (
    <RSVPContext.Provider value={contextValue}>
      {children}
    </RSVPContext.Provider>
  );
}; 